/* PDP delivery-date estimator.

   "Arrives <date>" headlining EXPRESS with a cutoff countdown, plus a STANDARD line. Detects the
   shopper's AU state from their browser timezone (no API), refined by an optional postcode box.
   Transit numbers are calibrated against live Shippit quotes from our Sydney warehouse (2128):
   Express is next-business-day almost everywhere, slower only to far-north QLD / regional WA / NT;
   Standard is Parcel Post (metro vs regional) + 1 day of leeway, matching checkout.

   All timing is computed in strict Sydney time (Intl, DST-safe) with NSW public holidays + weekends.

   ── TUNABLE BUSINESS DATA ───────────────────────────────────────────────────────────
   CUTOFF_HOUR     : Express same-business-day cutoff, Sydney time (3pm).
   STANDARD_LEEWAY : business days added to the Standard estimate (checkout adds +1).
   STANDARD        : Parcel Post transit [metro, regional] business days from Sydney, by state.
   isMetro()       : postcode ranges that count as capital-metro (drives the Standard split).
   expressDays()   : Express transit — 1 day, except far-north QLD (2), regional WA (3), NT (3).
   HOLIDAYS        : NSW public holidays (weekday ones; weekends handled separately). Extend yearly.
   Source: live Shippit quotes (Eparcel / Eparcel Express); NSW public holidays 2026–27.
   ──────────────────────────────────────────────────────────────────────────────────── */
(function () {
  var CUTOFF_HOUR = 15;      // 3pm Sydney
  var STANDARD_LEEWAY = 1;   // +1 business day on Standard (matches checkout)
  var ORIGIN = 'Sydney';

  // AusPost Express Post next-business-day network destination postcode ranges (origin 2128 is in it).
  // Standard (Parcel Post) transit business days from Sydney 2128, [metro, regional] per state.
  // Calibrated against live Shippit quotes (Eparcel). We add STANDARD_LEEWAY so the PDP matches
  // the checkout (which also adds +1). Metro is classified by postcode in isMetro().
  var STANDARD = {
    NSW: [2, 3], ACT: [2, 2], VIC: [3, 4], QLD: [3, 5], SA: [4, 5], WA: [5, 8], TAS: [5, 6], NT: [6, 6]
  };
  var STATE_LABEL = { NSW: 'NSW', ACT: 'ACT', VIC: 'VIC', QLD: 'QLD', SA: 'SA', WA: 'WA', TAS: 'TAS', NT: 'NT' };

  // NSW public holidays that fall on a weekday (block dispatch). Weekends handled separately.
  var HOLIDAYS = {
    '2026-01-01': 1, '2026-01-26': 1, '2026-04-03': 1, '2026-04-06': 1, '2026-04-27': 1,
    '2026-06-08': 1, '2026-10-05': 1, '2026-12-25': 1, '2026-12-28': 1,
    '2027-01-01': 1, '2027-01-26': 1, '2027-03-26': 1, '2027-03-29': 1, '2027-06-14': 1,
    '2027-10-04': 1, '2027-12-27': 1, '2027-12-28': 1
  };

  var TZ_STATE = {
    'Australia/Sydney': 'NSW', 'Australia/Lord_Howe': 'NSW', 'Australia/Broken_Hill': 'NSW',
    'Australia/Melbourne': 'VIC', 'Australia/Victoria': 'VIC', 'Australia/Brisbane': 'QLD',
    'Australia/Lindeman': 'QLD', 'Australia/Queensland': 'QLD', 'Australia/Adelaide': 'SA',
    'Australia/Perth': 'WA', 'Australia/Hobart': 'TAS', 'Australia/Currie': 'TAS',
    'Australia/Darwin': 'NT', 'Australia/Canberra': 'ACT', 'Australia/ACT': 'ACT',
    'Australia/NSW': 'NSW', 'Australia/Yancowinna': 'NSW'
  };

  function stateForTz() {
    try { return TZ_STATE[Intl.DateTimeFormat().resolvedOptions().timeZone] || null; }
    catch (e) { return null; }
  }

  function stateForPostcode(pc) {
    var n = parseInt(pc, 10);
    if (isNaN(n) || String(pc).length !== 4) return null;
    if ((n >= 1000 && n <= 1999) || (n >= 2000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'NSW';
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'ACT';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'VIC';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'QLD';
    if (n >= 5000 && n <= 5799) return 'SA';
    if (n >= 6000 && n <= 6797) return 'WA';
    if (n >= 7000 && n <= 7799) return 'TAS';
    if ((n >= 800 && n <= 899) || (n >= 900 && n <= 999)) return 'NT';
    return null;
  }

  // Capital-metro classification by postcode (drives the Standard metro/regional split).
  function isMetro(state, n) {
    if (!n) return state !== 'NT'; // no postcode: assume the capital metro (except Darwin)
    switch (state) {
      case 'NSW': return (n >= 1000 && n <= 1999) || (n >= 2000 && n <= 2249) || (n >= 2555 && n <= 2574) || (n >= 2745 && n <= 2786);
      case 'ACT': return true;
      case 'VIC': return (n >= 3000 && n <= 3207) || (n >= 8000 && n <= 8999);
      case 'QLD': return (n >= 4000 && n <= 4207) || (n >= 9000 && n <= 9499);
      case 'SA':  return (n >= 5000 && n <= 5199);
      case 'WA':  return (n >= 6000 && n <= 6199) || (n >= 6800 && n <= 6999);
      case 'TAS': return (n >= 7000 && n <= 7099);
      default: return false;
    }
  }

  // Express transit business days (Shippit: 1 almost everywhere from Sydney; slower only to
  // far-north QLD, regional WA and the NT).
  function expressDays(state, n, metro) {
    if (state === 'NT') return 3;
    if (state === 'WA' && !metro) return 3;
    if (state === 'QLD' && n && n >= 4700) return 2;
    return 1;
  }

  // Strict Sydney wall-clock parts (DST-safe via Intl).
  function sydParts() {
    var p = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
    return { y: +p.year, mo: +p.month, d: +p.day, h: (+p.hour) % 24, mi: +p.minute };
  }

  // Date-only math in UTC so adding days never drifts across DST.
  function pad(n) { return String(n).padStart(2, '0'); }
  function utcDate(y, mo, d) { return new Date(Date.UTC(y, mo - 1, d)); }
  function ymd(dt) { return dt.getUTCFullYear() + '-' + pad(dt.getUTCMonth() + 1) + '-' + pad(dt.getUTCDate()); }
  function isBiz(dt) { var w = dt.getUTCDay(); return w !== 0 && w !== 6 && !HOLIDAYS[ymd(dt)]; }
  function addDays(dt, n) { var x = new Date(dt); x.setUTCDate(x.getUTCDate() + n); return x; }
  function nextBiz(dt) { var x = addDays(dt, 1); while (!isBiz(x)) x = addDays(x, 1); return x; }
  function addBiz(dt, n) { var x = new Date(dt); for (var i = 0; i < n; i++) x = nextBiz(x); return x; }
  function fmtDate(dt) { return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); }
  function fmtDay(dt) { return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString('en-AU', { weekday: 'long' }); }

  function transit(state, pc) {
    var n = pc ? parseInt(pc, 10) : null;
    var metro = isMetro(state, n);
    var std = STANDARD[state] || [4, 6];
    return {
      express: expressDays(state, n, metro),
      standard: std[metro ? 0 : 1] + STANDARD_LEEWAY
    };
  }

  function compute(state, pc) {
    var s = sydParts();
    var today = utcDate(s.y, s.mo, s.d);
    var expShipsToday = isBiz(today) && s.h < CUTOFF_HOUR;
    var expDispatch = expShipsToday ? today : nextBiz(today);
    var stdDispatch = nextBiz(today); // Standard: dispatched next business day
    var hasZone = !!(state && STANDARD[state]);
    var t = transit(state, pc);

    var res = { hasDate: hasZone };
    if (hasZone) {
      res.express = addBiz(expDispatch, t.express);
      res.standard = addBiz(stdDispatch, t.standard);
      res.lead = 'Arrives ' + fmtDate(res.express);
      res.standardLine = 'Standard arrives ' + fmtDate(res.standard);
      res.location = (pc ? pc + ' ' : '') + (STATE_LABEL[state] || state);
    } else {
      res.lead = 'Fast shipping from ' + ORIGIN;
      res.standardLine = null;
      res.location = null;
    }

    if (expShipsToday) {
      var mins = CUTOFF_HOUR * 60 - (s.h * 60 + s.mi);
      var h = Math.floor(mins / 60), m = mins % 60;
      res.countdown = h > 0 ? (h + 'h ' + m + 'm') : (m + 'm');
      res.cutoff = 'Order within {C} to ship today';
    } else {
      res.countdown = null;
      res.cutoff = hasZone ? ('Express ships ' + fmtDay(expDispatch)) : 'Add your postcode for a delivery date';
    }
    return res;
  }

  function render(el, state, pc) {
    var r = compute(state, pc);
    var leadEl = el.querySelector('[data-de-lead]');
    var subEl = el.querySelector('[data-de-cutoff]');
    var stdEl = el.querySelector('[data-de-standard]');
    var locWrap = el.querySelector('[data-de-locwrap]');
    var locEl = el.querySelector('[data-de-location]');

    if (leadEl) leadEl.innerHTML = r.hasDate
      ? r.lead.replace(/(Arrives )(.*)/, '$1<strong>$2</strong>') + ' <span class="de__tag">Express</span>'
      : r.lead;

    if (subEl) {
      if (r.countdown != null) subEl.innerHTML = r.cutoff.replace('{C}', '<span class="de__count">' + r.countdown + '</span>');
      else subEl.textContent = r.cutoff;
    }
    if (stdEl) {
      if (r.standardLine) { stdEl.hidden = false; stdEl.textContent = r.standardLine; }
      else { stdEl.hidden = true; }
    }
    if (locWrap) {
      if (r.location) { locWrap.hidden = false; if (locEl) locEl.textContent = r.location; }
      else { locWrap.hidden = true; }
    }
    el.hidden = false;
  }

  function init(el) {
    if (el.__deInit) return;
    el.__deInit = true;

    var saved = null;
    try { saved = localStorage.getItem('insport:delivery-pc'); } catch (e) {}
    var savedState = saved ? stateForPostcode(saved) : null;
    el.__state = savedState || stateForTz();
    el.__pc = savedState ? saved : null;

    render(el, el.__state, el.__pc);
    setInterval(function () { render(el, el.__state, el.__pc); }, 60000);

    var changeBtn = el.querySelector('[data-de-change]');
    var form = el.querySelector('[data-de-form]');
    if (changeBtn && form) {
      changeBtn.addEventListener('click', function () {
        form.hidden = !form.hidden;
        if (!form.hidden) { var i = form.querySelector('input'); if (i) i.focus(); }
      });
    }
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = form.querySelector('input');
        var val = (input.value || '').trim();
        var st = stateForPostcode(val);
        var err = form.querySelector('[data-de-error]');
        if (!st) { if (err) err.hidden = false; return; }
        if (err) err.hidden = true;
        try { localStorage.setItem('insport:delivery-pc', val); } catch (e2) {}
        el.__state = st; el.__pc = val;
        render(el, st, val);
        form.hidden = true;
      });
      var inp = form.querySelector('input');
      if (inp) inp.addEventListener('input', function () { var err = form.querySelector('[data-de-error]'); if (err) err.hidden = true; });
    }

    setupDrive(el);
  }

  // Fun: the truck "drives" right along the road as the card scrolls up through the viewport,
  // and reverses on scroll up. Purely decorative — skipped for reduced-motion.
  function setupDrive(el) {
    try { if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; } catch (e) {}
    var truck = el.querySelector('[data-de-truck]');
    var card = el.querySelector('.de__card');
    if (!truck || !card) return;
    var raf = 0;
    function update() {
      raf = 0;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var rect = card.getBoundingClientRect();
      // Drive is tied to the CARD's position in the viewport, not page scroll, so it's at rest when
      // the card first comes into view (works on mobile where the card is below the fold and on
      // desktop where it's visible at load) and drives right as the card scrolls up. Reverses up.
      var max = card.clientWidth - 12 - (truck.offsetLeft + truck.offsetWidth); // 12px right padding
      if (max < 0) max = 0;
      var start = vh * 0.85; // card top at/below this = rest
      var end = vh * 0.15;   // card top at/above this = fully driven
      var p = (start - rect.top) / (start - end);
      p = p < 0 ? 0 : (p > 1 ? 1 : p);
      truck.style.transform = 'translateX(' + Math.round(p * max) + 'px)';
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(update); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function boot() { document.querySelectorAll('delivery-estimate').forEach(init); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
